import { faFileCsv, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CalculateIcon from '@mui/icons-material/Calculate';
import {
    Card,
    CardContent,
    Grid,
    IconButton,
    Tooltip as MuiTooltip,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    Typography,
    useTheme,
} from '@mui/material';
import dinero from 'dinero.js';
import { parse as parseCsv } from 'json2csv';
import moment from 'moment';
import { useMemo } from 'react';
import * as XLSX from 'xlsx';
import { SextforceMetricsCountersLastOfEveryMonth } from '../../../../hooks/useSextforceMetricsCountersLastOfEveryMonth';
import { OnlyFansMetricsSubscribedAtVsTotalSalesDetailsDataPoint } from '../../../../hooks/useSextforceMetricsSubscribedAtVsTotalSalesDetails';
import { d2f } from '../../../../utils/common';

// Interface for the monthly metrics row data
interface MonthlyMetricRow {
    month: string;
    monthDisplay: string;
    timestamp: number;
    initialSubscribers: number;
    subscribers: number;
    newSubscribers: number;
    initialNetRevenue: number;
    finalNetRevenue: number;
    netRevenue: number;
    initialTransitionsCount: number;
    newTransitionsCount: number;
    initialClaimsCount: number;
    newClaimsCount: number;
    initialSubscribersCount: number;
    newSubscribersCount: number;
}

interface Props {
    promoCampaignSubscribedAtVsSales: OnlyFansMetricsSubscribedAtVsTotalSalesDetailsDataPoint[] | undefined;
    promoCampaignSubscribedAtVsSalesLoading: boolean;
    countersLastOfEveryMonth: SextforceMetricsCountersLastOfEveryMonth[] | null | undefined;
    countersLastOfEveryMonthLoading: boolean;
    showEarningsWithSubscriptions: boolean;
    showEarningsAsGross: boolean;
    timezone: string;
    metricId: string;
    // Optional deduplicated cross-pollination earnings per month ('YYYY-MM' key, amount in
    // the displayed gross/net). null = that month's calculation failed. When the prop is
    // provided at all, the Cross-Poll. Earnings column is rendered.
    crossPollinationEarningsByMonth?: { [monthKey: string]: number | null };
    crossPollinationEarningsLoading?: boolean;
    // When provided, a Calculate button is rendered next to the export buttons to
    // trigger the cross-pollination earnings calculation
    onCalculateCrossPollinationEarnings?: () => void;
    calculateCrossPollinationEarningsDisabled?: boolean;
    calculateCrossPollinationEarningsTooltip?: string;
}

const SextforceMetricsSubscribedAtVsSalesSummary = (props: Props) => {
    const {
        promoCampaignSubscribedAtVsSales,
        promoCampaignSubscribedAtVsSalesLoading,
        countersLastOfEveryMonth,
        countersLastOfEveryMonthLoading,
        showEarningsWithSubscriptions,
        showEarningsAsGross,
        timezone,
        metricId,
        crossPollinationEarningsByMonth,
        crossPollinationEarningsLoading,
        onCalculateCrossPollinationEarnings,
        calculateCrossPollinationEarningsDisabled,
        calculateCrossPollinationEarningsTooltip,
    } = props;

    const showCrossPollinationColumn = crossPollinationEarningsByMonth !== undefined;

    const theme: Theme = useTheme();

    // Calculate monthly metrics from the subscription data
    const monthlyMetrics = useMemo(() => {
        if (!promoCampaignSubscribedAtVsSales || promoCampaignSubscribedAtVsSales.length === 0) {
            return [];
        }

        const filteredData = promoCampaignSubscribedAtVsSales;
        // .filter(
        //     row => row._id && row._id.interval && moment(row._id.interval).isAfter(row._id.createdAt),
        // );

        if (filteredData.length === 0) {
            return [];
        }

        // Filter out invalid dates
        const validData = filteredData.filter(
            row => row._id && row._id.interval /*&& moment(row._id.interval).isAfter(row._id.createdAt)*/,
        );

        // Sort data by date
        const sortedData = [...validData].sort((a, b) => moment(a._id.interval).valueOf() - moment(b._id.interval).valueOf());

        // Group data by month
        const monthlyData: { [key: string]: OnlyFansMetricsSubscribedAtVsTotalSalesDetailsDataPoint[] } = {};

        sortedData.forEach(d => {
            const date = moment.tz(d._id.interval, timezone); // ✅ Parse in user's timezone
            const monthKey = date.format('YYYY-MM');

            if (!monthlyData[monthKey]) {
                monthlyData[monthKey] = [];
            }

            monthlyData[monthKey].push(d);
        });

        // Calculate cumulative metrics for each month
        let cumulativeSubscribers = 0;
        let cumulativeRevenue = 0;
        const monthlyMetrics: MonthlyMetricRow[] = [];

        // Sort month keys chronologically
        const monthKeys = Object.keys(monthlyData).sort();

        monthKeys.forEach(monthKey => {
            const monthData = monthlyData[monthKey];
            const monthDate = moment.tz(monthKey + '-01', timezone); // ✅ Use timezone here too
            const monthDisplay = monthDate.format('MMM YYYY');

            // Calculate metrics for the month
            let monthSubscribers = 0;
            let monthRevenue = 0;

            monthData.forEach(d => {
                monthSubscribers += d.count;

                let revenue = 0;
                if (showEarningsWithSubscriptions) {
                    revenue = d.total ? (typeof d.total === 'number' ? d.total : d2f(d.total)) : 0;
                } else {
                    revenue = d.totalWithoutSubscriptions
                        ? typeof d.totalWithoutSubscriptions === 'number'
                            ? d.totalWithoutSubscriptions
                            : d2f(d.totalWithoutSubscriptions)
                        : 0;
                }

                if (!showEarningsAsGross) {
                    revenue = revenue * 0.8;
                }

                monthRevenue += revenue;
            });

            const counter = countersLastOfEveryMonth?.find(c => {
                return c.yearMonth === monthKey;
            });

            // Create metrics row
            const metricRow: MonthlyMetricRow = {
                month: monthKey,
                monthDisplay,
                timestamp: monthDate.valueOf(),
                initialSubscribers: cumulativeSubscribers,
                subscribers: monthSubscribers,
                newSubscribers: monthSubscribers,
                initialNetRevenue: cumulativeRevenue,
                finalNetRevenue: cumulativeRevenue + monthRevenue,
                netRevenue: monthRevenue,
                initialTransitionsCount: counter?.initialTransitionsCount || 0,
                newTransitionsCount: counter?.newTransitionsCount || 0,
                initialClaimsCount: counter?.initialClaimsCount || 0,
                newClaimsCount: counter?.newClaimsCount || 0,
                initialSubscribersCount: counter?.initialSubscribersCount || 0,
                newSubscribersCount: counter?.newSubscribersCount || 0,
            };

            // Update cumulative values for next month
            cumulativeSubscribers += monthSubscribers;
            cumulativeRevenue += monthRevenue;

            monthlyMetrics.push(metricRow);
        });

        return monthlyMetrics;
    }, [promoCampaignSubscribedAtVsSales, countersLastOfEveryMonth, showEarningsWithSubscriptions, showEarningsAsGross]);

    // Format currency values for display
    const formatCurrency = (value: number) => {
        return dinero({
            amount: value ? Math.trunc(value * 100) : 0,
            currency: 'USD',
        }).toFormat();
    };

    // Export functions
    const exportMonthlyData = (format: 'csv' | 'excel') => {
        const headers = [
            { label: 'Month', value: 'monthDisplay' },
            { label: 'Initial Subscribers (Found)', value: 'initialSubscribers' },
            { label: 'New Subscribers (Found)', value: 'newSubscribers' },
            { label: 'Total Subscribers (Found)', value: 'subscribers' },
            { label: 'New Clicks (OF Counters)', value: 'newTransitionsCount' },
            { label: 'Clicks at Month End (OF Counters)', value: 'initialTransitionsCount' },
            { label: 'New Claims (OF Counters)', value: 'newClaimsCount' },
            { label: 'Claims at Month End (OF Counters)', value: 'initialClaimsCount' },
            { label: 'New Subs (OF Counters)', value: 'newSubscribersCount' },
            { label: 'Subs at Month End (OF Counters)', value: 'initialSubscribersCount' },
            { label: 'Initial Net Revenue', value: 'initialNetRevenue' },
            { label: 'Net Revenue', value: 'netRevenue' },
            { label: 'Final Net Revenue', value: 'finalNetRevenue' },
            ...(showCrossPollinationColumn
                ? [
                      {
                          label: `Cross-Pollination Earnings ${showEarningsAsGross ? 'Gross' : 'Net'}`,
                          value: 'crossPollinationEarnings',
                      },
                  ]
                : []),
        ];

        // Prepare data for export
        const exportData = monthlyMetrics.map(row => {
            const crossEntry = showCrossPollinationColumn ? crossPollinationEarningsByMonth![row.month] : undefined;

            return {
                ...row,
                initialNetRevenue: formatCurrency(row.initialNetRevenue),
                netRevenue: formatCurrency(row.netRevenue),
                finalNetRevenue: formatCurrency(row.finalNetRevenue),
                ...(showCrossPollinationColumn
                    ? { crossPollinationEarnings: typeof crossEntry === 'number' ? formatCurrency(crossEntry) : '' }
                    : {}),
                timestamp: undefined, // Remove timestamp for export
                month: undefined, // Remove month for export
            };
        });

        const filename = `monthly_metrics_${metricId}_${moment().tz(timezone, true).format('YYYY-MM-DD')}`;

        if (format === 'csv') {
            // Format JSON data to CSV
            const csv = parseCsv(exportData, {
                fields: headers,
                transforms: [],
            });

            // Convert CSV to Blob and download
            const blob: Blob = new Blob([csv], { type: 'text/csv' });
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${filename}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode && link.parentNode.removeChild(link);
        } else if (format === 'excel') {
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet([]);
            XLSX.utils.sheet_add_aoa(worksheet, [headers.map(header => header.label)]);
            XLSX.utils.sheet_add_json(worksheet, exportData, {
                origin: 'A2',
                skipHeader: true,
                header: headers.map(header => header.value),
            });
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Monthly Metrics');
            XLSX.writeFile(workbook, `${filename}.xlsx`);
        }
    };

    const buttonsDisabled = promoCampaignSubscribedAtVsSalesLoading || countersLastOfEveryMonthLoading || monthlyMetrics.length === 0;

    return (
        <Card sx={{ width: '100%', marginBottom: theme.spacing(2) }}>
            <CardContent>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ marginBottom: theme.spacing(2) }}
                >
                    <Typography variant="h5">Monthly Summary</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {onCalculateCrossPollinationEarnings && (
                            <MuiTooltip
                                title={
                                    calculateCrossPollinationEarningsTooltip ||
                                    "Calculate every month's cross-pollination earnings, one month at a time"
                                }
                            >
                                <span>
                                    <IconButton
                                        disabled={calculateCrossPollinationEarningsDisabled}
                                        onClick={onCalculateCrossPollinationEarnings}
                                        aria-label="Calculate cross-pollination earnings"
                                    >
                                        <CalculateIcon
                                            fontSize="large"
                                            color={calculateCrossPollinationEarningsDisabled ? 'disabled' : 'secondary'}
                                        />
                                    </IconButton>
                                </span>
                            </MuiTooltip>
                        )}
                        <MuiTooltip title="Download as CSV">
                            <span>
                                <IconButton
                                    disabled={buttonsDisabled}
                                    onClick={() => exportMonthlyData('csv')}
                                    aria-label="Download as CSV"
                                >
                                    <FontAwesomeIcon
                                        icon={faFileCsv}
                                        size="2x"
                                        color={buttonsDisabled ? theme.palette.text.disabled : theme.palette.info.main}
                                    />
                                </IconButton>
                            </span>
                        </MuiTooltip>
                        <MuiTooltip title="Download as Excel">
                            <span>
                                <IconButton
                                    disabled={buttonsDisabled}
                                    onClick={() => exportMonthlyData('excel')}
                                    aria-label="Download as Excel"
                                >
                                    <FontAwesomeIcon
                                        icon={faFileExcel}
                                        size="2x"
                                        color={buttonsDisabled ? theme.palette.text.disabled : theme.palette.info.main}
                                    />
                                </IconButton>
                            </span>
                        </MuiTooltip>
                    </Stack>
                </Stack>

                <Typography variant="body1" sx={{ marginBottom: theme.spacing(2) }}>
                    This table shows monthly subscriber and revenue metrics, including starting values, new additions, and end-of-month
                    totals.
                    {!showEarningsAsGross ? <span> Revenue values are shown as net (after platform fees).</span> : null}
                </Typography>

                <Grid container>
                    <Grid item xs={12}>
                        <TableContainer component={Card}>
                            <Table stickyHeader aria-label="Monthly metrics table">
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            '& th': {
                                                fontWeight: 'bold',
                                                textWrap: 'pretty',
                                                backgroundColor: theme.palette.grey[200],
                                            },
                                        }}
                                    >
                                        <TableCell>Month</TableCell>
                                        <TableCell align="right">Initial Subscribers (Found)</TableCell>
                                        <TableCell align="right">New Subscribers (Found)</TableCell>
                                        <TableCell align="right">Total Subscribers (Found)</TableCell>
                                        <TableCell align="right">New Clicks (OF Counters)</TableCell>
                                        <TableCell align="right">Clicks at Month End (OF Counters)</TableCell>
                                        <TableCell align="right">New Claims (OF Counters)</TableCell>
                                        <TableCell align="right">Claims at Month End (OF Counters)</TableCell>
                                        <TableCell align="right">New Subs (OF Counters)</TableCell>
                                        <TableCell align="right">Subs at Month End (OF Counters)</TableCell>
                                        <TableCell align="right">Initial Net Revenue</TableCell>
                                        <TableCell align="right">Net Revenue</TableCell>
                                        <TableCell align="right">Final Net Revenue</TableCell>
                                        {showCrossPollinationColumn && (
                                            <TableCell align="right">
                                                <MuiTooltip title="Deduplicated earnings the fans first attributed to this group's links during this month spent on the selected cross-pollination account(s).">
                                                    <span>Cross-Poll. Earnings {showEarningsAsGross ? 'Gross' : 'Net'}</span>
                                                </MuiTooltip>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {promoCampaignSubscribedAtVsSalesLoading && (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center">
                                                <Skeleton variant="rectangular" width="100%" height={40} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {!promoCampaignSubscribedAtVsSalesLoading &&
                                        monthlyMetrics &&
                                        monthlyMetrics.length > 0 &&
                                        monthlyMetrics.map(row => (
                                            <TableRow key={row.month} hover>
                                                <TableCell sx={{ fontWeight: 'bold' }}>{row.monthDisplay}</TableCell>
                                                <TableCell align="right">{row.initialSubscribers.toLocaleString()}</TableCell>
                                                <TableCell align="right">{row.newSubscribers.toLocaleString()}</TableCell>
                                                <TableCell align="right">
                                                    {(row.initialSubscribers + row.newSubscribers).toLocaleString()}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {countersLastOfEveryMonthLoading ? (
                                                        <Skeleton width="100%" />
                                                    ) : (
                                                        row.newTransitionsCount.toLocaleString()
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {countersLastOfEveryMonthLoading ? (
                                                        <Skeleton width="100%" />
                                                    ) : (
                                                        row.initialTransitionsCount.toLocaleString()
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {countersLastOfEveryMonthLoading ? (
                                                        <Skeleton width="100%" />
                                                    ) : (
                                                        row.newClaimsCount.toLocaleString()
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {countersLastOfEveryMonthLoading ? (
                                                        <Skeleton width="100%" />
                                                    ) : (
                                                        row.initialClaimsCount.toLocaleString()
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {countersLastOfEveryMonthLoading ? (
                                                        <Skeleton width="100%" />
                                                    ) : (
                                                        row.newSubscribersCount.toLocaleString()
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {countersLastOfEveryMonthLoading ? (
                                                        <Skeleton width="100%" />
                                                    ) : (
                                                        row.initialSubscribersCount.toLocaleString()
                                                    )}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                                    {formatCurrency(row.initialNetRevenue)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                                    {formatCurrency(row.netRevenue)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                                    {formatCurrency(row.finalNetRevenue)}
                                                </TableCell>
                                                {showCrossPollinationColumn && (
                                                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                                        {(() => {
                                                            const entry = crossPollinationEarningsByMonth![row.month];

                                                            if (typeof entry === 'number') {
                                                                return formatCurrency(entry);
                                                            }

                                                            if (entry === undefined && crossPollinationEarningsLoading) {
                                                                return <Skeleton width="100%" />;
                                                            }

                                                            return '-';
                                                        })()}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    {!promoCampaignSubscribedAtVsSalesLoading && (!monthlyMetrics || monthlyMetrics.length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                No data available
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {monthlyMetrics.length > 0 && (
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                -
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                {monthlyMetrics.reduce((sum, row) => sum + row.newSubscribers, 0).toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                {(
                                                    monthlyMetrics[monthlyMetrics.length - 1]?.initialSubscribers +
                                                    monthlyMetrics[monthlyMetrics.length - 1]?.newSubscribers
                                                ).toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                {countersLastOfEveryMonthLoading ? (
                                                    <Skeleton width="100%" />
                                                ) : (
                                                    monthlyMetrics.reduce((sum, row) => sum + row.newTransitionsCount, 0).toLocaleString()
                                                )}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                -
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                {countersLastOfEveryMonthLoading ? (
                                                    <Skeleton width="100%" />
                                                ) : (
                                                    monthlyMetrics.reduce((sum, row) => sum + row.newClaimsCount, 0).toLocaleString()
                                                )}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                -
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                {countersLastOfEveryMonthLoading ? (
                                                    <Skeleton width="100%" />
                                                ) : (
                                                    monthlyMetrics.reduce((sum, row) => sum + row.newSubscribersCount, 0).toLocaleString()
                                                )}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                -
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                                                -
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                                                {formatCurrency(monthlyMetrics.reduce((sum, row) => sum + row.netRevenue, 0))}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                                                {formatCurrency(monthlyMetrics[monthlyMetrics.length - 1]?.finalNetRevenue || 0)}
                                            </TableCell>
                                            {showCrossPollinationColumn && (
                                                <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                                                    {crossPollinationEarningsLoading ? (
                                                        <Skeleton width="100%" />
                                                    ) : (
                                                        formatCurrency(
                                                            monthlyMetrics.reduce((sum, row) => {
                                                                const entry = crossPollinationEarningsByMonth![row.month];

                                                                return sum + (typeof entry === 'number' ? entry : 0);
                                                            }, 0),
                                                        )
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default SextforceMetricsSubscribedAtVsSalesSummary;
